import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import announcementServices from 'ApiService/Common/announcementServices';
import { audienceLabel } from './helpers';

const emptyForm = { title: '', message: '', audience: '', branch: '', batch: '' };

// While editing, "keep" leaves the announcement pointed at its current audience —
// useful when an admin edits a teacher's batch post they could not target themselves.
export const KEEP = 'keep';

const SELECT_CLASS =
  'w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 dark:!border-white/10 dark:!bg-navy-800 dark:text-white dark:disabled:!bg-navy-900';

const batchLabel = (batch) => {
  const branches = batch.branches?.map((b) => b.name).join(', ');
  return `Batch #${batch.batchNumber}${branches ? ` — ${branches}` : ''}${
    batch.mode ? ` (${batch.mode})` : ''
  }`;
};

/**
 * Create / edit form, shared by the Announcements page and the dashboard card's
 * edit modal. `editing` is the announcement being changed, or null to create one.
 */
const AnnouncementForm = ({ editing, targets, onSaved, onCancel }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Course -> batch structure -> batch, loaded one level at a time.
  const [branchFilter, setBranchFilter] = useState('');
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [structures, setStructures] = useState([]);
  const [structureId, setStructureId] = useState('');
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState({
    courses: false,
    structures: false,
    batches: false,
  });

  // Only superadmin and HR cover more than one branch, so only they need a filter.
  const showBranchFilter = (targets.branches?.length || 0) > 1;

  const resetCascade = () => {
    setCourses([]);
    setCourseId('');
    setStructures([]);
    setStructureId('');
    setBatches([]);
  };

  useEffect(() => {
    if (editing) {
      setForm({
        title: editing.title,
        message: editing.message,
        audience: KEEP,
        branch: '',
        batch: '',
      });
    } else {
      // Targets can land after the user has started typing, so only fill in the
      // default audience — never clobber a title or message already entered.
      setForm((prev) => ({
        ...prev,
        audience: prev.audience || targets.scopes?.[0] || '',
      }));
    }
    resetCascade();
    setBranchFilter('');
  }, [editing, targets.scopes]);

  const fetchCourses = async (branch) => {
    setLoading((prev) => ({ ...prev, courses: true }));
    try {
      const response = await announcementServices.listCourses(
        branch || undefined
      );
      if (response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load courses');
    }
    setLoading((prev) => ({ ...prev, courses: false }));
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // "all" and "branch" need no extra lookup; "batch" opens the cascade.
  const handleAudienceTypeChange = (event) => {
    const audience = event.target.value;
    setForm((prev) => ({ ...prev, audience, branch: '', batch: '' }));
    resetCascade();
    if (audience === 'batch') {
      fetchCourses(branchFilter);
    }
  };

  const handleBranchFilterChange = (event) => {
    const branch = event.target.value;
    setBranchFilter(branch);
    setForm((prev) => ({ ...prev, batch: '' }));
    resetCascade();
    fetchCourses(branch);
  };

  const handleCourseChange = async (event) => {
    const nextCourseId = event.target.value;
    setCourseId(nextCourseId);
    setStructureId('');
    setStructures([]);
    setBatches([]);
    setForm((prev) => ({ ...prev, batch: '' }));
    if (!nextCourseId) return;

    setLoading((prev) => ({ ...prev, structures: true }));
    try {
      const response = await announcementServices.listBatchStructures(
        nextCourseId
      );
      if (response.data.success) {
        setStructures(response.data.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Failed to load batch structures'
      );
    }
    setLoading((prev) => ({ ...prev, structures: false }));
  };

  const handleStructureChange = async (event) => {
    const nextStructureId = event.target.value;
    setStructureId(nextStructureId);
    setBatches([]);
    setForm((prev) => ({ ...prev, batch: '' }));
    if (!nextStructureId) return;

    setLoading((prev) => ({ ...prev, batches: true }));
    try {
      const response = await announcementServices.listBatches(
        courseId,
        nextStructureId,
        branchFilter || undefined
      );
      if (response.data.success) {
        setBatches(response.data.data || []);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load batches');
    }
    setLoading((prev) => ({ ...prev, batches: false }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (!form.audience) {
      toast.error('Please select who should see this announcement');
      return;
    }
    if (form.audience === 'branch' && !form.branch) {
      toast.error('Please select a branch');
      return;
    }
    if (form.audience === 'batch' && !form.batch) {
      toast.error('Please select a batch');
      return;
    }

    const payload = {
      title: form.title.trim(),
      message: form.message.trim(),
      // Omitting `audience` tells the API to keep the current one.
      ...(form.audience !== KEEP && { audience: form.audience }),
      ...(form.audience === 'branch' && { branch: form.branch }),
      ...(form.audience === 'batch' && { batch: form.batch }),
    };

    setSubmitting(true);
    try {
      const response = editing
        ? await announcementServices.update(editing._id, payload)
        : await announcementServices.create(payload);

      if (response.data.success) {
        toast.success(response.data.message);
        if (!editing) {
          setForm({ ...emptyForm, audience: targets.scopes?.[0] || '' });
          resetCascade();
        }
        onSaved(response.data.data, Boolean(editing));
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${editing ? 'update' : 'post'} announcement`
      );
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
          Title
        </label>
        <input
          type="text"
          value={form.title}
          onChange={handleChange('title')}
          maxLength={150}
          placeholder="Announcement title"
          className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-blue-500 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
          Message
        </label>
        <textarea
          rows={4}
          value={form.message}
          onChange={handleChange('message')}
          placeholder="Write your announcement..."
          className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-blue-500 dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
          Who should see this?
        </label>
        {/* Only the scopes this role owns are offered; the API re-checks on save. */}
        <select
          value={form.audience}
          onChange={handleAudienceTypeChange}
          className={SELECT_CLASS}
        >
          {editing && (
            <option value={KEEP}>Keep current — {audienceLabel(editing)}</option>
          )}
          {!editing && <option value="">Select audience</option>}
          {targets.scopes?.includes('all') && (
            <option value="all">All branches (every student)</option>
          )}
          {targets.scopes?.includes('branch') && (
            <option value="branch">A branch</option>
          )}
          {targets.scopes?.includes('batch') && (
            <option value="batch">A batch</option>
          )}
        </select>
      </div>

      {form.audience === 'branch' && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
            Branch
          </label>
          <select
            value={form.branch}
            onChange={handleChange('branch')}
            className={SELECT_CLASS}
          >
            <option value="">Select branch</option>
            {targets.branches?.map((branch) => (
              <option key={branch._id} value={branch._id}>
                {branch.name}
                {branch.location ? ` (${branch.location})` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {form.audience === 'batch' && (
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4 dark:border-white/10">
          {showBranchFilter && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
                Branch filter
              </label>
              <select
                value={branchFilter}
                onChange={handleBranchFilterChange}
                className={SELECT_CLASS}
              >
                <option value="">All my branches</option>
                {targets.branches?.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                    {branch.location ? ` (${branch.location})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
              Course
            </label>
            <select
              value={courseId}
              onChange={handleCourseChange}
              disabled={loading.courses}
              className={SELECT_CLASS}
            >
              <option value="">
                {loading.courses ? 'Loading courses...' : 'Select course'}
              </option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.courseName}
                </option>
              ))}
            </select>
            {!loading.courses && !courses.length && (
              <p className="mt-1 text-xs text-red-500">
                No courses are available to you.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
              Batch Structure
            </label>
            <select
              value={structureId}
              onChange={handleStructureChange}
              disabled={!courseId || loading.structures}
              className={SELECT_CLASS}
            >
              <option value="">
                {!courseId
                  ? 'Pick a course first'
                  : loading.structures
                  ? 'Loading structures...'
                  : 'Select batch structure'}
              </option>
              {structures.map((structure) => (
                <option key={structure._id} value={structure._id}>
                  {structure.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-300">
              Batch
            </label>
            <select
              value={form.batch}
              onChange={handleChange('batch')}
              disabled={!structureId || loading.batches}
              className={SELECT_CLASS}
            >
              <option value="">
                {!structureId
                  ? 'Pick a batch structure first'
                  : loading.batches
                  ? 'Loading batches...'
                  : 'Select batch'}
              </option>
              {batches.map((batch) => (
                <option key={batch._id} value={batch._id}>
                  {batchLabel(batch)}
                </option>
              ))}
            </select>
            {structureId && !loading.batches && !batches.length && (
              <p className="mt-1 text-xs text-red-500">
                No batches of yours run under this structure.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-blue-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-blue-600 disabled:opacity-60"
        >
          {submitting
            ? editing
              ? 'Updating...'
              : 'Posting...'
            : editing
            ? 'Update Announcement'
            : 'Post Announcement'}
        </button>
        {onCancel && editing && (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default AnnouncementForm;
